// Import core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Misc dependencices.
import { capitalize } from '../../../../common/utils/string.js';

// Config.
import Enums from '../../config/enums.js';

// Model objects.
import { Entity } from '../../model.js';

/**
 * <pre>
 * Goal -> ProgressBarSegment module React class.
 *
 * This component outputs markup to render a Segment within the Progress Bar for the Goal component.
 * </pre>
 *
 * @author Tim Loram
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:Goal.Components.Elements
 * @namespace
 *
 * @return {module:Goal.Components.Elements.ProgressBarSegment}
 */
class ProgressBarSegment extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /**
   * Render
   *
   * @return {module:Goal.Components.Elements.ProgressBarSegment}
   */
  render() {
    // Define mandatory segment wrapper data attributes.
    const _wrapperData = {
      'data-ngx-percent': this.props.percent,
    };

    // Append optional segment wrapper data attributes.
    switch (this.props.formatting.displayStatStrategy) {
      case Enums.GOAL_STAT_DISPLAY_STRATEGY_VALUE:
        if (this.props.value !== null) {
          _wrapperData['data-ngx-value'] = this.props.value;
        }
        break;

      case Enums.GOAL_STAT_DISPLAY_STRATEGY_VALUEGOAL:
        if (this.props.value !== null) {
          _wrapperData['data-ngx-value'] = this.props.value;
        }
        if (this.props.goal !== null) {
          _wrapperData['data-ngx-goal'] = this.props.goal;
        }
        break;
    }

    /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
    // Calculate segment wrapper dimensions based on proportion and segmentSize mode.
    let _wrapperDimensions;

    switch (this.props.segmentSize) {
      // Display segements proportionally sized.
      case 'proportional':
        const _proportionalPercent = this.props.proportion;

        if (this.props.orientation === 'horizontal') {
          _wrapperDimensions = {
            width: _proportionalPercent + '%',
          };
        } else if (this.props.orientation === 'vertical') {
          _wrapperDimensions = {
            height: _proportionalPercent + '%',
          };
        }

        break;

      // Display segements equally sized.
      case 'equal':
      default:
        break;
    }

    /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
    // Calculate segment progress bar dimensions based on percentage.
    let _progressBarDimensions;

    // Don't allow width percentage to exceed 100 (which is possible if overflow voting is enabled for this goal).
    const _percent = this.props.percent <= 100 ? this.props.percent : 100;

    if (this.props.orientation === 'horizontal') {
      _progressBarDimensions = {
        width: _percent + '%',
      };
    } else if (this.props.orientation === 'vertical') {
      _progressBarDimensions = {
        height: _percent + '%',
      };
    }

    /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
    // Define segment label (if set).
    let _segmentLabelHorizontal;
    let _segmentLabelVertical;
    if (this.props.label) {
      const _segmentLabel = (
        <div className="xSegmentLabel xCopyContainer">
          <p className="xCopy xLabel">{this.props.label}</p>
        </div>
      );

      if (this.props.orientation === 'horizontal') {
        _segmentLabelHorizontal = _segmentLabel;
      } else if (this.props.orientation === 'vertical') {
        _segmentLabelVertical = _segmentLabel;
      }
    }

    /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
    // Merge internal classes with props.
    let _wrapperClasses = 'xProgressBarSegmentWrapper';
    _wrapperClasses += ' xSegmentSize' + capitalize(this.props.segmentSize);
    if (this.props.wrapperClasses) {
      _wrapperClasses += ' ' + this.props.wrapperClasses;
    }

    let _barClasses = 'xBar';
    if (this.props.barClasses) {
      _barClasses += ' ' + this.props.barClasses;
    }

    let _valueClasses = 'xValue xCopyContainer';
    if (this.props.valueClasses) {
      _valueClasses += ' ' + this.props.valueClasses;
    }

    const _formattedPercent = this.formatPercent(this.props.percent, this.props.formatting.decimalPlaces);

    return (
      <div className={_wrapperClasses} {..._wrapperData} style={_wrapperDimensions}>
        <div className="xSegmentBar">
          <div className={_barClasses} style={_progressBarDimensions} />
          <div className={_valueClasses}>
            <span className="xCopy xPercent">{_formattedPercent}%</span>
          </div>
          {_segmentLabelVertical}
        </div>

        {_segmentLabelHorizontal}
      </div>
    );
  }

  /**
   * Format percentage to a given number of decimal places.
   *
   * @return {String}
   */
  formatPercent(percent, decimalPlaces) {
    let formattedPercent;

    percent = percent.toString();
    if (decimalPlaces > 0) {
      // Pad string to 2 decimal places if required.
      const decimalPlaceIndex = percent.indexOf('.');
      if (decimalPlaceIndex > -1) {
        const precision = percent.slice(decimalPlaceIndex + 1).length;
        if (precision < 2) {
          percent += '0';
        }
      } else {
        percent += '.00';
      }

      formattedPercent = percent.slice(0, percent.indexOf('.') + (decimalPlaces + 1));
    } else {
      formattedPercent = parseInt(percent);
    }

    return formattedPercent;
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} orientation The progress bar orientation.
 * @property {String} segmentSize The sizing mode for the segment.
 * @property {module:Goal.Model.Entity.Formatting} formatting Object defining a various formatting options (eg. no of decimal places to show progress percentages).
 */
ProgressBarSegment.propTypes = {
  orientation: PropTypes.string,
  segmentSize: PropTypes.string,
  formatting:  PropTypes.instanceOf(Entity.Formatting),
};

// Export the dumb component.
export default ProgressBarSegment;
